import type { PermissionModule } from '@/types'

export interface RouteModuleEntry {
  prefix: string
  module: PermissionModule
}

/**
 * Longest-prefix match: path → permission module.
 * Keep in sync with tigerflow-api ModuleRoutePermissions / api.php wiring.
 */
export const ROUTE_MODULE_MAP: RouteModuleEntry[] = [
  { prefix: '/dashboard', module: 'dashboard' },
  { prefix: '/calendar', module: 'agenda' },
  { prefix: '/jobs', module: 'agenda' },
  { prefix: '/clients', module: 'clients' },
  { prefix: '/quotes', module: 'quotes' },
  { prefix: '/invoices', module: 'invoices' },
  { prefix: '/credit-notes', module: 'invoices' },
  { prefix: '/payments', module: 'payments' },
  { prefix: '/contracts', module: 'contracts' },
  { prefix: '/tasks', module: 'tasks' },
  { prefix: '/technicians', module: 'technicians' },
  { prefix: '/settings', module: 'settings' },
]

/** Resolve the module for a pathname, or null if ungated. */
export function moduleForPath(pathname: string): PermissionModule | null {
  const raw = pathname.startsWith('/') ? pathname : `/${pathname}`
  const path = raw.split(/[?#]/, 1)[0] || '/'
  let best: RouteModuleEntry | null = null
  for (const entry of ROUTE_MODULE_MAP) {
    if (path === entry.prefix || path.startsWith(`${entry.prefix}/`)) {
      if (!best || entry.prefix.length > best.prefix.length) {
        best = entry
      }
    }
  }
  return best?.module ?? null
}

/** First portal path the user can view, used as redirect fallback. */
export function firstAccessiblePath(
  permissions: string[] | undefined | null
): string {
  const order = ROUTE_MODULE_MAP.map(e => e.module)
  const seen = new Set<PermissionModule>()
  for (const module of order) {
    if (seen.has(module)) continue
    seen.add(module)
    if (permissions?.includes(`${module}.view`)) {
      const entry = ROUTE_MODULE_MAP.find(e => e.module === module)
      return entry?.prefix ?? '/dashboard'
    }
  }
  return '/login'
}
